if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.button__text');
        this.submitButtonPrice = this.submitButton.querySelector('.button__price');
        this.submitButtonCurrent = this.submitButtonText.innerHTML;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Initialize simple property validation
        this.initSimplePropertyValidation();
      }

      initSimplePropertyValidation() {
        this.simplePropertyFields = this.collectSimplePropertyFields();
        
        if (this.simplePropertyFields.length === 0) return;
        
        // Attach input listeners to clear errors
        this.simplePropertyFields.forEach(field => {
          if (field.type === 'checkbox') {
            field.element.addEventListener('change', () => this.clearSimpleFieldError(field));
          } else {
            field.element.addEventListener('input', () => this.clearSimpleFieldError(field));
            field.element.addEventListener('change', () => this.clearSimpleFieldError(field));
          }
        });
      }

      collectSimplePropertyFields() {
        const fields = [];
        const productInfo = this.closest('product-info');
        if (!productInfo) return fields;
        
        // Collect all simple property fields
        productInfo.querySelectorAll('.simple-property-field').forEach(fieldWrapper => {
          const input = fieldWrapper.querySelector('input[name^="properties["], select[name^="properties["]');
          if (!input) return;
          
          const isRequired = input.hasAttribute('required') || input.classList.contains('required');
          if (!isRequired) return;
          
          const propertyName = this.extractPropertyName(input.name);
          const label = this.getFieldLabel(fieldWrapper, input);
          
          fields.push({
            element: input,
            wrapper: fieldWrapper,
            propertyName,
            label,
            type: input.type === 'checkbox' ? 'checkbox' : (input.tagName.toLowerCase() === 'select' ? 'select' : 'text')
          });
        });
        
        return fields;
      }

      extractPropertyName(name) {
        // Extract property name from "properties[Property Name]"
        const match = name.match(/properties\[(.*?)\]/);
        return match ? match[1] : name;
      }

      getFieldLabel(wrapper, input) {
        // Try to find label text
        const label = wrapper.querySelector('label');
        if (label) {
          // Remove asterisk and trim
          return label.textContent.replace('*', '').trim();
        }
        
        // Fallback to placeholder or property name
        return input.placeholder || this.extractPropertyName(input.name);
      }

      validateSimpleProperties() {
        if (this.simplePropertyFields.length === 0) return true;
        
        let isValid = true;
        let firstInvalidField = null;

        this.simplePropertyFields.forEach(field => {
          const fieldValid = this.validateSimpleField(field);
          if (!fieldValid) {
            isValid = false;
            if (!firstInvalidField) {
              firstInvalidField = field.element;
            }
          }
        });

        // Focus first invalid field if form submission fails
        if (!isValid && firstInvalidField) {
          setTimeout(() => {
            firstInvalidField.focus();
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }

        return isValid;
      }

      validateSimpleField(field) {
        let isValid = false;

        switch (field.type) {
          case 'checkbox':
            isValid = field.element.checked;
            break;
          
          case 'select':
            isValid = field.element.value && field.element.value.trim() !== '';
            break;
          
          case 'text':
          default:
            isValid = field.element.value && field.element.value.trim() !== '';
            break;
        }

        if (!isValid) {
          console.log('invalid firing 1');
          this.showSimpleFieldError(field, 'This field is required');
        } else {
          this.clearSimpleFieldError(field);
        }

        return isValid;
      }

      showSimpleFieldError(field, message) {
        const errorId = `simple-property-error-${field.propertyName.replace(/[^a-zA-Z0-9]/g, '-')}`;
        let errorEl = field.wrapper.querySelector('.contact__field-error');
        
        if (!errorEl) {
          errorEl = document.createElement('small');
          errorEl.className = 'contact__field-error';
          errorEl.id = errorId;
          errorEl.style.cssText = 'display: block; margin-top: 0.5rem;';
          errorEl.innerHTML = `
            <span class="visually-hidden">Error</span>
            <span class="form__message">
              <span class="svg-wrapper">${this.getErrorIcon()}</span>
              ${message}
            </span>
          `;
          field.wrapper.appendChild(errorEl);
        } else {
          // Update existing error message
          const messageSpan = errorEl.querySelector('.form__message');
          if (messageSpan) {
            const iconHTML = `<span class="svg-wrapper">${this.getErrorIcon()}</span>`;
            messageSpan.innerHTML = iconHTML + message;
          }
        }
        
        field.element.classList.add('error');
        field.element.setAttribute('aria-invalid', 'true');
        field.element.setAttribute('aria-describedby', errorId);
      }

      clearSimpleFieldError(field) {
        const errorEl = field.wrapper.querySelector('.contact__field-error');
        if (errorEl) {
          errorEl.remove();
        }
        
        field.element.classList.remove('error');
        field.element.removeAttribute('aria-invalid');
        field.element.removeAttribute('aria-describedby');
      }

      getErrorIcon() {
        // Return the error icon SVG (matching theme's icon-error.svg)
        return `<svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
          <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
          <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"/>
          <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"/>
          <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7">
        </svg>`;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Validate simple properties first
        if (!this.validateSimpleProperties()) {
          return;
        }

        // Validate PropertyModifiers before proceeding
        const productInfo = this.closest('product-info');
        if (productInfo) {
          const modifierContainer = productInfo.querySelector('[data-property-modifiers]');
          if (modifierContainer && modifierContainer._propertyModifiersInstance) {
            const isValid = modifierContainer._propertyModifiersInstance.validateProperties();
            if (!isValid) {
              return; // Stop submission if validation fails
            }
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
            setTimeout(function(){ 
             if (HulkappWishlist) {
               HulkappWishlist._setSaveForLater();
             } 
             }, 2000);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, price = null) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          // Hide price when disabled
          if (this.submitButtonPrice) {
            this.submitButtonPrice.style.display = 'none';
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButtonCurrent;
          // Update and show price when enabled
          if (this.submitButtonPrice && price) {
            this.submitButtonPrice.textContent = price;
            this.submitButtonPrice.style.display = '';
          }
        }
      }

      updateButtonPrice(price) {
        if (this.submitButtonPrice && price) {
          this.submitButtonPrice.textContent = price;
          this.submitButton.dataset.price = price;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}